import "./dialog.scss";
import * as React from "react";
import { connect, type ConnectedProps } from "react-redux";
import type { ThunkDispatch } from "redux-thunk";
import {
    DIALOG_CLICK,
    type DialogOptions,
    type DialogTypes,
    hideDialog,
} from "../../Actions/Dialogs/DialogActions";
import type { RootState } from "../../Reducers/ShopfrontReducers";
import { fireEventType } from "../../Utilities/Generic/ActionsHelper";
import { UILayerConsumer } from "../Misc/UILayerConsumer";
import DialogContent from "./DialogContent";

const connector = connect((state: RootState) => ({
    header : state.dialog.header,
    content: state.dialog.content,
    type   : state.dialog.dialogType,
    options: (
        typeof state.dialog.options === "undefined" ?
            false :
            state.dialog.options
    ) as false | Array<DialogOptions>,
    open         : state.dialog.visible,
    focus        : state.dialog.focus,
    closeable    : state.dialog.closeable,
    showFrame    : state.dialog.showFrame,
    hasBackground: state.dialog.hasBackground,
    hideDelay    : state.dialog.delay,
    overLogin    : state.dialog.overLogin,
}));

type PropsFromRedux = ConnectedProps<typeof connector> & {
    dispatch: ThunkDispatch<any, any, any>;
};

export class Dialog extends React.Component<PropsFromRedux> {
    public dialogReference = React.createRef<HTMLDivElement>();

    public dialogTimeout: false | ReturnType<typeof setTimeout> = false;

    public componentDidMount(): void {
        this.initialiseDialog();
        window.addEventListener("keydown", this.catchCancel);
        window.addEventListener("message", this.handleMessage);
    }

    public componentWillUnmount(): void {
        window.removeEventListener("keydown", this.catchCancel);
        window.removeEventListener("message", this.handleMessage);
    }

    public componentDidUpdate(prevProps: Readonly<PropsFromRedux>): void {
        this.initialiseDialog();

        if(prevProps.hideDelay !== this.props.hideDelay) {
            if(this.dialogTimeout) {
                clearTimeout(this.dialogTimeout);
                this.dialogTimeout = false;
            }

            if(typeof this.props.hideDelay !== "undefined") {
                this.dialogTimeout = setTimeout(() => {
                    this.props.dispatch(hideDialog());
                }, this.props.hideDelay);
            }
        }

        if(this.props.open && this.dialogTimeout !== false) {
            clearTimeout(this.dialogTimeout);
        }
    }

    public handleMessage = (e: MessageEvent): void => {
        if(!this.props.open) {
            return;
        }

        if(e.data === "INTEGRATION_DIALOG_CLOSE") {
            this.handleHide();
        }
    };

    public initialiseDialog = (): void => {
        if(this.props.focus && this.props.open && this.dialogReference.current) {
            const inputElement = this.dialogReference.current.querySelector("input, textarea, select") as HTMLElement;

            if(inputElement) {
                inputElement.focus();
            } else {
                this.dialogReference.current.focus();
            }
        }
    };

    public catchClick = (e: React.MouseEvent<HTMLDivElement>): void => {
        this.props.dispatch(fireEventType(DIALOG_CLICK));
        window.dispatchEvent(new CustomEvent(DIALOG_CLICK, { detail: e.target }));
        e.stopPropagation();
    };

    public catchCancel = (e: KeyboardEvent): void => {
        if(this.props.open) {
            if(e.keyCode === 27 || e.key === "Escape") {
                e.stopPropagation();

                if(this.props.closeable) {
                    this.props.dispatch(hideDialog());
                } else {
                    e.preventDefault();
                }
            }

            if(e.keyCode === 13 || e.key === "Enter") {
                if(!this.props.closeable) {
                    e.preventDefault();
                }
            }
        }
    };

    public handleHide = (): void => {
        // Hide the dialog
        this.props.dispatch(hideDialog());
    };

    public render(): React.ReactNode {
        let content;
        const classes: Array<string> = [ this.props.type as string ];

        if(this.props.showFrame) {
            classes.push("show-frame");

            content = (
                <DialogContent
                    header={this.props.header as string}
                    type={this.props.type as DialogTypes}
                    content={this.props.content as React.ReactNode}
                    options={this.props.options}
                    onHide={this.handleHide}
                />
            );
        } else {
            classes.push("no-frame");
            content = this.props.content;
        }

        if(!this.props.hasBackground) {
            classes.push("no-background");
        }

        const containerClasses = [];

        if(this.props.open) {
            containerClasses.push("open");
        }

        let zIndex = 2;

        if(this.props.overLogin) {
            containerClasses.push("over-login");
            zIndex = 6;
        }

        return (
            <UILayerConsumer zIndex={zIndex}>
                <div
                    id="dialog-container"
                    className={containerClasses.join(" ")}
                >
                    <div
                        id="dialog"
                        className={classes.join(" ")}
                        onClick={this.catchClick}
                        ref={this.dialogReference}
                    >
                        {content}
                    </div>
                </div>
                {((!this.props.closeable || this.props.overLogin) && this.props.open) && (
                    <div id="dialog-backdrop" className={(this.props.overLogin ? "over-login" : "")} />
                )}
            </UILayerConsumer>
        );
    }
}

export default connector(Dialog);
